import type {
  Attachment as GQLAttachment,
  WhitelabelConfig as GQLItem,
  WhitelabelAttribution,
} from '../../../generated/internal.graphql';
import { take } from '../../../util/takePayload';
import { type Attachment, gqlStatusMap } from '../../Attachment/Attachment';

/**
 * A whitelabel branding configuration — the per-domain brand a host serves under — as the
 * superadmin screens read and write it on the internal (VP staff) schema.
 *
 * Replaces the legacy `admin/src/types/Whitelabel.ts` (legacy interface: `apiClient`,
 * `generated/graphql`, nested under `app { … }`), which ADR-009 moved straight to InternalSchema
 * without passing through AppSchema.
 */

export type WhitelabelConfigType = {
  id: number;
  domain: string;
  name?: string;
  landingUrl?: string;
  fromEmail?: string;
  stripeApiKey?: string;
  stripePublishableKey?: string;
  checkoutUrlBase?: string;
  attribution: WhitelabelAttribution;
  attachment?: Attachment;
};

/**
 * Mirrors the internal `WhitelabelAttributes` input.
 *
 * There are no stripe keys: the backend derives the publishable key and returns the secret one
 * masked, and the input type does not accept either — so carrying them here would claim a write
 * that never happens. The legacy store deleted them from its payload by hand for the same reason.
 *
 * `attribution` is optional because the input is nullable: omitting it leaves the stored state alone.
 */
export type WhitelabelConfigInput = {
  id?: number;
  name?: string;
  domain?: string;
  landingUrl?: string;
  fromEmail?: string;
  checkoutUrlBase?: string;
  attribution?: WhitelabelAttribution;
  attachmentId?: number;
};

/**
 * The shared `Attachment.fromGql` is bound to the *legacy* generated type, so the internal schema
 * needs its own mapper. Only the mapper is local: the domain type and its status map are shared,
 * because both schemas emit the same `AttachmentStatusEnum` wire values and the form component that
 * consumes this already binds to that type. `WhitelabelConfig.spec.ts` pins the status values so a
 * future divergence fails there rather than rendering an unknown status.
 */
const attachmentFromGql = (item: GQLAttachment): Attachment => ({
  ...take<Attachment>(item, 'id', 'name', 'mime', 'size', 'checksum'),
  // `url` is nullable on the internal schema and optional here, and `take` passes `null` straight
  // through — it only skips `undefined`.
  url: item.url ?? undefined,
  status: item.status ? gqlStatusMap[item.status] : undefined,
});

export const fromGql = (item: GQLItem): WhitelabelConfigType => ({
  ...take<WhitelabelConfigType>(item, 'id', 'domain', 'attribution'),
  // The rest are nullable on the internal schema; map them so no `null` reaches a `T | undefined`.
  name: item.name ?? undefined,
  landingUrl: item.landingUrl ?? undefined,
  fromEmail: item.fromEmail ?? undefined,
  stripeApiKey: item.stripeApiKey ?? undefined,
  stripePublishableKey: item.stripePublishableKey ?? undefined,
  checkoutUrlBase: item.checkoutUrlBase ?? undefined,
  attachment: item.attachment ? attachmentFromGql(item.attachment) : undefined,
});

export const fromGqlCollection = (items: GQLItem[]): WhitelabelConfigType[] => items.map(fromGql);
